import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  confirmEdgeConnector,
  getEdgeTagCatalog,
  inspectEdgeTagCatalog,
  listEdgeConnectorHistory,
  listEdgeConnectors,
  previewEdgeConnector,
  previewEdgeConnectorLifecycle,
  previewEdgeConnectorRollback,
  retryEdgeConnector,
} from "../../../api";
import { useNotify } from "../../../ToastContext";
import linkIcon from "../../../assets/link.svg";
import refreshIcon from "../../../assets/refresh.svg";
import { MappingEditor, NumberField, FieldError, errorMessage } from "./plc-protocol-server-fields";
import {
  createMapping,
  protocolServerFormFromConfig,
} from "./plc-protocol-servers-model";
import {
  buildEdgeModbusServerSpec,
  displayEdgeRevision,
  edgeModbusSecurityHistoryLabel,
  edgeModbusReviewMode,
  edgeModbusReviewPresentation,
  edgeModbusServerReadiness,
  edgeModbusServerTruth,
  normalizeEdgeModbusServerInventory,
  reviewMatchesEdgeModbusServerSpec,
  validateEdgeModbusServerForm,
} from "./plc-modbus-edge-server-model";
import {
  classifyEdgeTag,
  currentEdgeTagEvidence,
  edgeTagCatalogPresentation,
  edgeTagCatalogReview,
  edgeTagRequirement,
  eligibleEdgeTagEntries,
  normalizeEdgeTagCatalog,
} from "./plc-edge-tag-catalog-model";
import { SYNC_LABELS, syncPillClass } from "./EdgeConnectorsModal";

const EMPTY_EDGE_FORM = {
  ...protocolServerFormFromConfig({
    enabled: false,
    listen_address: "127.0.0.1:1502",
    unit_id: 1,
    max_clients: 16,
    request_timeout_ms: 5000,
    idle_timeout_ms: 300000,
    source_cidrs: [],
    mappings: [],
  }),
  listenHost: "",
  idleTimeoutMs: "300000",
};

const shortDigest = (value) => {
  const digest = String(value || "");
  return digest.length === 64 ? `${digest.slice(0, 12)}…${digest.slice(-8)}` : "—";
};
const formatDateTime = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Never" : date.toLocaleString();
};
const chunks = (values, size) => {
  const result = [];
  for (let index = 0; index < values.length; index += size) result.push(values.slice(index, index + size));
  return result;
};

export const edgeModbusOperationAvailability = ({ desiredRunning, ready }) => ({
  lifecycleReview: desiredRunning || ready,
  retry: !desiredRunning || ready,
});

const publicConfigForm = (server) => {
  const config = server?.public_configuration;
  if (!config || typeof config !== "object") return null;
  const mappings = Array.isArray(server.public_mappings) ? server.public_mappings : null;
  if (!mappings) return null;
  return {
    ...protocolServerFormFromConfig({
      ...config,
      enabled: server.desired_state === "running",
      mappings,
    }),
    idleTimeoutMs: String(config.idle_timeout_ms ?? 300000),
  };
};

export const edgeModbusLifecycleSpec = (server, desiredState) => {
  const savedForm = publicConfigForm(server);
  if (!savedForm) return null;
  return {
    ...buildEdgeModbusServerSpec(savedForm, server.connector_id),
    desired_state: desiredState,
  };
};

const catalogReviewForSpec = (spec, catalog, evidenceByPath = {}) => edgeTagCatalogReview({
  enabled: spec?.desired_state === "running",
  mappings: Array.isArray(spec?.mappings) ? spec.mappings.map((mapping) => ({
    table: mapping?.native_config?.table,
    valueType: mapping?.native_config?.value_type,
    tagPath: mapping?.tag_path,
  })) : [],
}, catalog, evidenceByPath);

const catalogEvidenceSnapshot = (catalog, evidenceByPath) => ({
  connection: catalog?.connection,
  catalogFresh: catalog?.catalogFresh === true,
  catalogSha256: catalog?.catalogSha256 || "",
  byPath: evidenceByPath || {},
});

export const edgeModbusCatalogReviewForReview = (review, catalog, fallbackEvidence = {}) => {
  if (!review?.spec) return { valid: false, results: [], message: "The reviewed server revision is unavailable." };
  if (review.spec.desired_state !== "running") return catalogReviewForSpec(review.spec, catalog);
  const scoped = review.catalogEvidence;
  if (!scoped) return catalogReviewForSpec(review.spec, catalog, fallbackEvidence);
  if (
    catalog?.connection !== "online" || !catalog?.catalogFresh ||
    scoped.connection !== catalog.connection || !scoped.catalogFresh ||
    !scoped.catalogSha256 || scoped.catalogSha256 !== catalog.catalogSha256
  ) {
    return {
      valid: false,
      results: [],
      message: "Runtime tag evidence changed or became stale after this review was created. Refresh and review the exact revision again.",
    };
  }
  return catalogReviewForSpec(review.spec, catalog, scoped.byPath);
};

export function EdgeModbusServerReview({ review, gatewayName, busy, acknowledged, catalogReview, onAcknowledgedChange, onCancel, onConfirm }) {
  if (!review?.preview || !review?.spec) return null;
  const { preview } = review;
  const config = preview.public_configuration;
  const mappings = Array.isArray(preview.public_mappings) ? preview.public_mappings : [];
  return (
    <div className="gwOverlay" style={{ zIndex: 115 }} onClick={busy ? undefined : onCancel}>
      <div className="gwModal plcEdgeModbusReview" role="dialog" aria-modal="true" aria-labelledby="edge-modbus-review-title" onClick={(event) => event.stopPropagation()}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle" id="edge-modbus-review-title">Review Modbus TCP server revision</div>
            <div className="gwModalSubtitle">Nothing reaches {gatewayName} until you confirm this exact signed revision.</div>
          </div>
        </div>
        <div className="gwModalBody">
          <div className="plcProtocolRuntimeSummary">
            <div><span>Runtime</span><strong>{gatewayName}</strong></div>
            <div><span>Desired</span><strong>{preview.desired_state === "running" ? "Listening" : "Stopped"}</strong></div>
            <div><span>Revision</span><strong>{preview.revision}</strong></div>
            <div><span>Digest</span><strong title={preview.sha256}>{shortDigest(preview.sha256)}</strong></div>
          </div>
          <div className="gwNotice isSoft">
            <strong>{config.listen_address} · Unit {config.unit_id}</strong>
            <span>{config.source_cidrs.length} allowed source network{config.source_cidrs.length === 1 ? "" : "s"} · {mappings.length} tag mapping{mappings.length === 1 ? "" : "s"}</span>
            <span>Maximum {config.max_clients} clients · request timeout {config.request_timeout_ms} ms · idle timeout {config.idle_timeout_ms} ms</span>
          </div>
          {config.source_cidrs.length ? (
            <div className="plcEdgeModbusReviewList">
              <strong>Allowed source networks</strong>
              {config.source_cidrs.map((cidr) => <code key={cidr}>{cidr}</code>)}
            </div>
          ) : null}
          <div className="plcEdgeModbusReviewList">
            <strong>Tag address map</strong>
            {mappings.length === 0 ? <span>No tags exposed.</span> : mappings.map((mapping) => (
              <span key={`${mapping.tag_path}:${mapping.table}:${mapping.address}`}><code>{mapping.tag_path}</code><img src={linkIcon} alt="" aria-hidden="true" className="gwIcon plcMappingLink" />{mapping.table}:{mapping.address}:{mapping.value_type} · {mapping.flow === "observe" ? "Read only" : "Read / write"}</span>
            ))}
          </div>
          <div className="gwNotice isSoft">
            Confirmation is bound to the server-issued revision and digest. Queued means desired, not listening; only a matching applied device receipt can show the listener as active.
          </div>
          <div className="gwNotice isSoft">
            Every mapped path must already exist in {gatewayName}'s local tag store through a deployed PLC program, GPIO point, or client connector. Cloud catalog presence alone does not prove edge availability; a missing edge tag rejects apply and is reported as a device diagnostic.
          </div>
          {preview.desired_state === "running" && catalogReview?.valid === false ? (
            <div className="gwAlert" role="alert">
              <div className="gwAlertTitle">Fresh runtime tag evidence required</div>
              <div className="gwAlertBody">{catalogReview.message}</div>
            </div>
          ) : null}
          {preview.no_application_authentication_acknowledgement_required ? (
            <label className="plcEdgeModbusSecurityAcknowledgement">
              <input
                type="checkbox"
                checked={acknowledged}
                disabled={busy}
                onChange={(event) => onAcknowledgedChange(event.target.checked)}
              />
              <span>I understand Modbus TCP has no application authentication, encryption, or integrity. This listener requires a specific source allowlist and network segmentation.</span>
            </label>
          ) : null}
        </div>
        <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
          <button className="gwGhostBtn" type="button" disabled={busy} onClick={onCancel}>Cancel</button>
          <button className="gwPrimaryBtn" type="button" disabled={busy || catalogReview?.valid === false || (preview.no_application_authentication_acknowledgement_required && !acknowledged)} onClick={onConfirm}>{busy ? "Queuing…" : "Confirm and queue"}</button>
        </div>
      </div>
    </div>
  );
}

export function EdgeModbusServerConfiguration({ gateway, canConfigure, initialProposal }) {
  const notify = useNotify();
  const [form, setForm] = useState(EMPTY_EDGE_FORM);
  const [touched, setTouched] = useState(false);
  const [baseline, setBaseline] = useState("");
  const [inventory, setInventory] = useState(null);
  const [tagCatalog, setTagCatalog] = useState(null);
  const [tagCatalogError, setTagCatalogError] = useState("");
  const [tagEvidenceByPath, setTagEvidenceByPath] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [review, setReview] = useState(null);
  const [reviewAcknowledged, setReviewAcknowledged] = useState(false);
  const [history, setHistory] = useState([]);
  const loadGeneration = useRef(0);
  const tagEvidenceGeneration = useRef(0);
  const proposalApplied = useRef(false);

  const spec = useMemo(() => buildEdgeModbusServerSpec(form, inventory?.server?.connector_id), [form, inventory?.server?.connector_id]);
  const fingerprint = useMemo(() => JSON.stringify(spec), [spec]);
  const dirty = baseline ? fingerprint !== baseline : !inventory?.server;
  const validation = useMemo(() => validateEdgeModbusServerForm(form), [form]);
  // Field errors stay quiet until the user edits — a not-yet-configured
  // server starts empty, and painting "required" errors on a pristine form
  // reads as a broken page. The review CTA still gates on full validation.
  const shownErrors = touched ? validation.formErrors : {};
  const shownMappingErrors = touched ? validation.mappingErrors : [];
  const catalogReview = useMemo(() => edgeTagCatalogReview(form, tagCatalog, tagEvidenceByPath), [form, tagCatalog, tagEvidenceByPath]);
  const mappingCatalogStatus = useMemo(() => form.mappings.map((mapping) =>
    classifyEdgeTag(currentEdgeTagEvidence(tagCatalog, tagEvidenceByPath[String(mapping.tagPath || "").trim()]), mapping.tagPath, edgeTagRequirement(mapping))
  ), [form.mappings, tagCatalog, tagEvidenceByPath]);
  const catalogPresentation = useMemo(() => edgeTagCatalogPresentation(tagCatalog), [tagCatalog]);
  const reviewValid = validation.valid && catalogReview.valid;
  const reviewMessage = validation.valid ? catalogReview.message : validation.message;
  const readiness = useMemo(() => edgeModbusServerReadiness(
    inventory || {},
    spec.requirements.capabilities,
  ), [inventory, spec.requirements.capabilities]);
  const truth = useMemo(() => edgeModbusServerTruth(inventory || {}), [inventory]);
  const reviewMode = edgeModbusReviewMode({ dirty, present: truth.present, syncState: truth.syncState });
  const reviewPresentation = edgeModbusReviewPresentation(reviewMode);
  const hasEditableSnapshot = !inventory?.server || !!publicConfigForm(inventory.server);
  const editable = canConfigure && readiness.ready && hasEditableSnapshot && !busy;
  const operationAvailability = edgeModbusOperationAvailability({ desiredRunning: truth.desiredRunning, ready: readiness.ready });

  const load = useCallback(async ({ preserveForm = false } = {}) => {
    const generation = ++loadGeneration.current;
    setLoading(true);
    try {
      const [inventoryResponse, catalogResponse] = await Promise.allSettled([
        listEdgeConnectors(gateway.id),
        getEdgeTagCatalog(gateway.id, { limit: 500 }),
      ]);
      if (generation !== loadGeneration.current) return;
      if (catalogResponse.status === "fulfilled") {
        setTagCatalog(normalizeEdgeTagCatalog(catalogResponse.value));
        setTagEvidenceByPath({});
        setTagCatalogError("");
      } else {
        setTagCatalog(null);
        setTagCatalogError(errorMessage(catalogResponse.reason, "Could not read this runtime's tag catalog."));
      }
      if (inventoryResponse.status === "rejected") throw inventoryResponse.reason;
      const result = normalizeEdgeModbusServerInventory(inventoryResponse.value);
      setInventory(result);
      setError("");
      const nextForm = publicConfigForm(result.server);
      if (result.server && nextForm) {
        const nextSpec = buildEdgeModbusServerSpec(nextForm, result.server.connector_id);
        setBaseline(JSON.stringify(nextSpec));
        if (!preserveForm) {
          setForm(nextForm);
          setTouched(false);
        }
      } else if (!result.server) {
        setBaseline("");
        if (!preserveForm) {
          setForm(EMPTY_EDGE_FORM);
          setTouched(false);
        }
      }
    } catch (loadError) {
      if (generation !== loadGeneration.current) return;
      setError(errorMessage(loadError, "Could not read the selected runtime's signed connector inventory."));
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [gateway.id]);

  useEffect(() => {
    proposalApplied.current = false;
    setReview(null);
    setReviewAcknowledged(false);
    setHistory([]);
    setBaseline("");
    setForm(EMPTY_EDGE_FORM);
    setTouched(false);
    setTagCatalog(null);
    setTagCatalogError("");
    setTagEvidenceByPath({});
    load();
    const timer = window.setInterval(() => load({ preserveForm: true }), 5000);
    return () => {
      loadGeneration.current += 1;
      window.clearInterval(timer);
    };
  }, [load]);

  useEffect(() => {
    const generation = ++tagEvidenceGeneration.current;
    const catalogDigest = tagCatalog?.catalogSha256;
    const paths = [...new Set(form.mappings.map((mapping) => String(mapping.tagPath || "").trim()).filter(Boolean))];
    if (!tagCatalog?.catalogFresh || !catalogDigest || paths.length === 0) {
      setTagEvidenceByPath({});
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const nextEvidence = {};
      try {
        for (const pathChunk of chunks(paths, 500)) {
          if (cancelled || generation !== tagEvidenceGeneration.current) return;
          const evidence = normalizeEdgeTagCatalog(await inspectEdgeTagCatalog(gateway.id, pathChunk));
          if (evidence.catalogSha256 !== catalogDigest) throw new Error("Runtime tag catalog changed while validating mapped paths.");
          pathChunk.forEach((path) => { nextEvidence[path] = evidence; });
        }
        if (!cancelled && generation === tagEvidenceGeneration.current) setTagEvidenceByPath(nextEvidence);
      } catch (_error) {
        if (!cancelled && generation === tagEvidenceGeneration.current) setTagEvidenceByPath({});
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [form.mappings, gateway.id, tagCatalog]);

  useEffect(() => {
    if (proposalApplied.current || !initialProposal || initialProposal.gateway_id !== gateway.id || loading) return;
    proposalApplied.current = true;
    setTouched(true);
    setForm((current) => ({
      ...current,
      enabled: initialProposal.desired_state === "running",
      listenHost: String(initialProposal.listen_host || current.listenHost),
      listenPort: String(initialProposal.listen_port ?? current.listenPort),
      unitId: String(initialProposal.unit_id ?? current.unitId),
      maxClients: String(initialProposal.max_clients ?? current.maxClients),
      requestTimeoutMs: String(initialProposal.request_timeout_ms ?? current.requestTimeoutMs),
      idleTimeoutMs: String(initialProposal.idle_timeout_ms ?? current.idleTimeoutMs),
      sourceCidrsText: Array.isArray(initialProposal.source_cidrs) ? initialProposal.source_cidrs.join("\n") : current.sourceCidrsText,
      mappings: Array.isArray(initialProposal.mappings) ? initialProposal.mappings.map((mapping) => createMapping({
        table: mapping.table,
        address: mapping.address,
        tagPath: mapping.tag_path,
        valueType: mapping.value_type,
        byteOrder: mapping.byte_order,
        wordOrder: mapping.word_order,
      })) : current.mappings,
    }));
    notify({ type: "info", title: "AI proposal ready for review", message: `Review every endpoint, network, and tag mapping before creating a signed revision for ${gateway.name}.` });
  }, [gateway.id, gateway.name, initialProposal, loading, notify]);

  const setField = (field) => (event) => {
    const value = field === "enabled" ? event : event.target.value;
    setTouched(true);
    setForm((current) => ({ ...current, [field]: value }));
  };
  const changeMapping = (index, patch) => {
    setTouched(true);
    setForm((current) => ({
      ...current,
      mappings: current.mappings.map((mapping, candidate) => candidate === index ? { ...mapping, ...patch } : mapping),
    }));
  };
  const removeMapping = (index) => {
    setTouched(true);
    setForm((current) => ({ ...current, mappings: current.mappings.filter((_, candidate) => candidate !== index) }));
  };
  const addMapping = () => {
    setTouched(true);
    setForm((current) => ({ ...current, mappings: [...current.mappings, createMapping()] }));
  };

  const previewConfiguration = async () => {
    if (!reviewValid || !readiness.ready || !editable) return;
    setBusy("preview");
    try {
      const preview = await previewEdgeConnector(gateway.id, spec, inventory?.server?.desired_revision);
      if (!reviewMatchesEdgeModbusServerSpec(preview, spec)) {
        throw new Error("The server review did not echo the exact Modbus listener resources and mappings. Nothing was queued.");
      }
      setReview({
        preview,
        spec,
        catalogEvidence: spec.desired_state === "running" ? catalogEvidenceSnapshot(tagCatalog, tagEvidenceByPath) : null,
      });
      setReviewAcknowledged(false);
    } catch (previewError) {
      notify({ type: "error", title: "Edge revision could not be reviewed", message: errorMessage(previewError, "The signed connector preview was rejected.") });
    } finally {
      setBusy("");
    }
  };

  const previewLifecycle = async () => {
    if (!inventory?.server) return;
    const desired = truth.desiredRunning ? "stopped" : "running";
    if (desired === "running" && !readiness.ready) return;
    const savedForm = publicConfigForm(inventory.server);
    let lifecycleSpec = edgeModbusLifecycleSpec(inventory.server, desired);
    if (desired === "running" && (!savedForm || !lifecycleSpec)) return;
    if (desired === "running") {
      const startForm = { ...savedForm, enabled: true };
      const startValidation = validateEdgeModbusServerForm(startForm);
      if (!startValidation.valid) {
        notify({
          type: "error",
          title: "Running review is blocked",
          message: startValidation.message,
        });
        return;
      }
    }
    setBusy("lifecycle");
    try {
      let lifecycleEvidence = {};
      if (desired === "running") {
        if (!tagCatalog?.catalogFresh) throw new Error("Fresh runtime tag evidence is required before the saved listener can be started.");
        const paths = [...new Set(savedForm.mappings.map((mapping) => String(mapping.tagPath || "").trim()).filter(Boolean))];
        const exactEvidence = {};
        for (const pathChunk of chunks(paths, 500)) {
          const evidence = normalizeEdgeTagCatalog(await inspectEdgeTagCatalog(gateway.id, pathChunk));
          if (evidence.catalogSha256 !== tagCatalog.catalogSha256) {
            throw new Error("Runtime tag catalog changed while the saved listener mappings were validated. Refresh and review again.");
          }
          pathChunk.forEach((path) => { exactEvidence[path] = evidence; });
        }
        lifecycleEvidence = exactEvidence;
        const startCatalogReview = edgeTagCatalogReview({ ...savedForm, enabled: true }, tagCatalog, exactEvidence);
        if (!startCatalogReview.valid) throw new Error(startCatalogReview.message);
      }
      const preview = await previewEdgeConnectorLifecycle(gateway.id, inventory.server.connector_id, desired);
	  if (desired === "stopped" && !lifecycleSpec) {
		const previewForm = publicConfigForm({
		  public_configuration: preview.public_configuration,
		  public_mappings: preview.public_mappings,
		  desired_state: preview.desired_state,
		});
		lifecycleSpec = previewForm ? buildEdgeModbusServerSpec(previewForm, inventory.server.connector_id) : null;
	  }
	  if (!lifecycleSpec) throw new Error("The lifecycle preview did not include a complete public server projection.");
      if (!reviewMatchesEdgeModbusServerSpec(preview, lifecycleSpec)) throw new Error("The lifecycle review did not match the current server revision.");
      setReview({
        preview,
        spec: lifecycleSpec,
        catalogEvidence: desired === "running" ? catalogEvidenceSnapshot(tagCatalog, lifecycleEvidence) : null,
      });
      setReviewAcknowledged(false);
    } catch (lifecycleError) {
      notify({ type: "error", title: "Lifecycle review failed", message: errorMessage(lifecycleError, "Could not review the server lifecycle revision.") });
    } finally {
      setBusy("");
    }
  };

  const confirmReview = async () => {
    if (!review?.preview) return;
    const currentCatalogReview = edgeModbusCatalogReviewForReview(review, tagCatalog, tagEvidenceByPath);
    if (!currentCatalogReview.valid) {
      notify({ type: "error", title: "Fresh runtime tag evidence required", message: currentCatalogReview.message });
      return;
    }
    if (review.preview.no_application_authentication_acknowledgement_required && !reviewAcknowledged) {
      notify({ type: "error", title: "Security acknowledgement required", message: "Acknowledge the lack of Modbus application security and the required network controls before queuing this listener." });
      return;
    }
    setBusy("confirm");
    try {
      await confirmEdgeConnector(gateway.id, review.preview, {
        acknowledge_no_application_authentication: reviewAcknowledged,
      });
      notify({ type: "success", title: "Signed server revision queued", message: `${gateway.name} must report the same applied revision and digest before the listener appears active.` });
      setReview(null);
      setReviewAcknowledged(false);
      await load();
    } catch (confirmError) {
      notify({ type: "error", title: "Revision was not queued", message: errorMessage(confirmError, "The exact reviewed revision could not be confirmed.") });
    } finally {
      setBusy("");
    }
  };

  const retry = async () => {
    if (!inventory?.server || (truth.desiredRunning && !readiness.ready)) return;
    setBusy("retry");
    try {
      await retryEdgeConnector(gateway.id, inventory.server.connector_id);
      notify({ type: "success", title: "Reconciliation queued", message: "The exact desired revision will be offered again; this does not prove the listener is active." });
      await load({ preserveForm: true });
    } catch (retryError) {
      notify({ type: "error", title: "Retry failed", message: errorMessage(retryError, "The desired revision was not queued again.") });
    } finally {
      setBusy("");
    }
  };

  const loadHistory = async () => {
    if (!inventory?.server) return;
    setBusy("history");
    try {
      const result = await listEdgeConnectorHistory(gateway.id, inventory.server.connector_id);
      setHistory(Array.isArray(result?.revisions) ? result.revisions : []);
    } catch (historyError) {
      notify({ type: "error", title: "Revision history unavailable", message: errorMessage(historyError, "Could not read Modbus server revision history.") });
    } finally {
      setBusy("");
    }
  };

  const previewRollback = async (revision) => {
    setBusy(`rollback:${revision}`);
    try {
      const preview = await previewEdgeConnectorRollback(gateway.id, inventory.server.connector_id, revision);
      const publicConfiguration = preview.public_configuration;
      const rollbackForm = publicConfiguration ? publicConfigForm({
        public_configuration: publicConfiguration,
        public_mappings: preview.public_mappings,
        desired_state: preview.desired_state,
      }) : null;
      if (!rollbackForm) throw new Error("The rollback preview did not include a complete public Modbus server configuration.");
      const rollbackSpec = buildEdgeModbusServerSpec(rollbackForm, inventory.server.connector_id);
	  if (rollbackSpec.desired_state === "running") {
		if (!readiness.ready) throw new Error("Fresh runtime capability and tag-identity evidence is required to restore a running listener revision.");
		const rollbackCatalogReview = edgeTagCatalogReview(rollbackForm, tagCatalog, tagEvidenceByPath);
		if (!rollbackCatalogReview.valid) throw new Error(rollbackCatalogReview.message);
	  }
      if (!reviewMatchesEdgeModbusServerSpec(preview, rollbackSpec)) throw new Error("The rollback preview did not match its public Modbus server configuration.");
      setReview({
        preview,
        spec: rollbackSpec,
        catalogEvidence: rollbackSpec.desired_state === "running" ? catalogEvidenceSnapshot(tagCatalog, tagEvidenceByPath) : null,
      });
      setReviewAcknowledged(false);
    } catch (rollbackError) {
      notify({ type: "error", title: "Rollback review failed", message: errorMessage(rollbackError, "Could not prepare the historical server revision.") });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="plcEdgeModbusServer" data-gw-ai-form-control="reviewed-edge-modbus-server">
      <div className="gwNotice isSoft plcEdgeModbusTargetTruth">
        <strong>{readiness.ready ? `Fresh capability snapshot · ${gateway.name}` : gateway.name}</strong>
        <span>{readiness.message}</span>
        <span>Last device heartbeat: {formatDateTime(inventory?.lastSeenAt)}</span>
      </div>
      {error ? <div className="gwAlert" role="alert"><div className="gwAlertTitle">Edge server inventory unavailable</div><div className="gwAlertBody">{error}</div></div> : null}
      {loading && !inventory ? <div className="gwHint">Reading signed connector state…</div> : null}

      {inventory ? (
        <div className="gwCardSection">
          <div className="gwSectionTitle plcProtocolSectionTitle">
            <span>Modbus TCP server · {gateway.name}</span>
            <div className="gwHeaderActions">
              <span className={`gwStatusPill ${syncPillClass(truth.syncState)}`}>{truth.listening ? "Listening" : (SYNC_LABELS[truth.syncState] || "State unknown")}</span>
              <button className="gwIconButton" type="button" disabled={!!busy} onClick={() => load({ preserveForm: dirty })} aria-label="Refresh server state" title="Refresh"><img src={refreshIcon} alt="" className="gwIcon" /></button>
            </div>
          </div>
          <div className="gwCardSectionBody">
            <div className="plcProtocolRuntimeSummary">
              <div><span>Desired revision</span><strong>{displayEdgeRevision(truth.desiredRevision)}</strong></div>
              <div><span>Applied revision</span><strong>{displayEdgeRevision(truth.appliedRevision)}</strong></div>
              <div><span>Desired state</span><strong>{truth.present ? (truth.desiredRunning ? "Listening" : "Stopped") : "Not configured"}</strong></div>
              <div><span>Device state</span><strong>{truth.actualState === "unknown" ? "Not reported" : truth.actualState}</strong></div>
            </div>
            {!truth.reconciled && truth.present ? <div className="gwNotice isSoft"><strong>Desired and device state diverge</strong><span>{truth.diagnostic || "The device has not confirmed the exact desired revision and digest."}</span></div> : null}
            {truth.present ? (
              <div className="gwHeaderActions plcEdgeModbusActions">
                <button className="gwSecondaryBtn" type="button" disabled={!canConfigure || !operationAvailability.retry || !!busy} onClick={retry}>{busy === "retry" ? "Retrying…" : "Retry desired"}</button>
                <button className="gwSecondaryBtn" type="button" disabled={!canConfigure || !operationAvailability.lifecycleReview || !!busy || (!truth.desiredRunning && !hasEditableSnapshot)} onClick={previewLifecycle}>{busy === "lifecycle" ? "Preparing…" : truth.desiredRunning ? "Review stop…" : "Review start…"}</button>
                <button className="gwSecondaryBtn" type="button" disabled={!!busy} onClick={loadHistory}>{busy === "history" ? "Loading…" : "History"}</button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {inventory?.server && !hasEditableSnapshot ? (
        <div className="gwAlert" role="alert">
          <div className="gwAlertTitle">Current edge configuration is not available for safe editing</div>
          <div className="gwAlertBody">The runtime inventory did not provide the complete public listener and mapping projection. Existing desired/applied truth remains visible, but replacement is blocked so mappings are never silently discarded.</div>
        </div>
      ) : null}

      {inventory && hasEditableSnapshot ? (
        <>
          <div className="gwCardSection">
            <div className="gwSectionTitle plcProtocolSectionTitle"><span>Listener configuration</span>{dirty && touched ? <span className="plcProtocolUnsaved">Draft changes</span> : null}</div>
            <div className="gwCardSectionBody">
              <div className="gwToggleRow plcProtocolEnableRow">
                <div><div className="gwLabel">Desired listener state</div><div className="gwHint">Running opens the selected edge device interface only after exact review, confirmation, delivery, and device application.</div></div>
                <button type="button" className={`gwToggle ${form.enabled ? "isOn" : ""} ${!editable ? "isDisabled" : ""}`} role="switch" aria-checked={form.enabled} aria-label="Run edge Modbus TCP server" disabled={!editable} onClick={() => setField("enabled")(!form.enabled)}><span /></button>
              </div>
              <div className="plcProtocolConfigGrid">
                <div className="gwField plcProtocolListenHost">
                  <label className="gwLabel">Device listen IP address</label>
                  <input className="gwInput" value={form.listenHost} onChange={setField("listenHost")} placeholder="10.0.0.170" disabled={!editable} aria-invalid={!!shownErrors.listenHost} />
                  <div className="gwHint">One concrete unicast address present on {gateway.name}; wildcard and hostname listeners are rejected.</div>
                  <FieldError>{shownErrors.listenHost}</FieldError>
                </div>
                <NumberField label="TCP port" value={form.listenPort} onChange={setField("listenPort")} min={1024} max={65535} step={1} disabled={!editable} hint="Edge service ports are limited to 1024–65535." error={shownErrors.listenPort} />
                <NumberField label="Unit ID" value={form.unitId} onChange={setField("unitId")} min={0} max={255} step={1} disabled={!editable} error={shownErrors.unitId} />
                <NumberField label="Maximum clients" value={form.maxClients} onChange={setField("maxClients")} min={1} max={1024} step={1} disabled={!editable} error={shownErrors.maxClients} />
                <NumberField label="Request timeout (ms)" value={form.requestTimeoutMs} onChange={setField("requestTimeoutMs")} min={100} max={60000} step={100} disabled={!editable} error={shownErrors.requestTimeoutMs} />
                <NumberField label="Idle client timeout (ms)" value={form.idleTimeoutMs} onChange={setField("idleTimeoutMs")} min={1000} max={3600000} step={1000} disabled={!editable} error={shownErrors.idleTimeoutMs} />
              </div>
              <div className="gwField plcProtocolSourceCidrs">
                <label className="gwLabel">Allowed source networks (CIDR)</label>
                <textarea className="gwInput" rows={3} value={form.sourceCidrsText} onChange={setField("sourceCidrsText")} placeholder="10.0.0.0/24" disabled={!editable} aria-invalid={!!shownErrors.sourceCidrs} />
                <div className="gwHint">Specific client networks only. A running edge listener rejects an empty or catch-all allowlist.</div>
                <FieldError>{shownErrors.sourceCidrs}</FieldError>
              </div>
            </div>
          </div>
          <div className="gwCardSection">
            <div className="gwSectionTitle plcProtocolSectionTitle"><div><span>Tag address map</span><small>{form.mappings.length} mapping{form.mappings.length === 1 ? "" : "s"}</small></div><button className="gwSecondaryBtn plcProtocolAddButton" type="button" disabled={!editable} onClick={addMapping}>Add mapping</button></div>
            <div className="gwCardSectionBody">
              <div className="gwNotice isSoft plcEdgeTagCatalogTruth">
                <div className="plcEdgeTagCatalogHeading">
                  <strong>Selected-runtime tag catalog</strong>
                  <span className={`gwStatusPill ${catalogPresentation.tone}`}>{catalogPresentation.label}</span>
                </div>
                <span>{tagCatalogError || catalogPresentation.message}</span>
                <span>Suggestions include only paths proven present in both {gateway.name} and the Control Seat catalog with the exact type and access this mapping needs. You can still type a path manually, but a Running review stays blocked until every path is freshly eligible.</span>
              </div>
              {form.mappings.length === 0 ? <div className="gwEmptyState gwEmptyCompact"><div className="gwEmptyTitle">No tags exposed</div><div className="gwEmptyBody">A stopped revision may keep an empty map. To run the listener, add at least one normal Control Seat tag path before review.</div></div> : (
                <div className="plcProtocolMappings">{form.mappings.map((mapping, index) => {
                  const listId = `plc-edge-tag-paths-${index}`;
                  const suggestions = eligibleEdgeTagEntries(tagCatalog, mapping);
                  return (
                    <React.Fragment key={mapping.key}>
                      <MappingEditor
                        mapping={mapping}
                        index={index}
                        error={shownMappingErrors[index]}
                        disabled={!editable}
                        tagListId={listId}
                        tagInputMode="manual"
                        tagStatus={mappingCatalogStatus[index]}
                        onChange={changeMapping}
                        onRemove={removeMapping}
                      />
                      <datalist id={listId}>{suggestions.map((entry) => <option value={entry.path} key={entry.path} />)}</datalist>
                    </React.Fragment>
                  );
                })}</div>
              )}
              {!canConfigure ? <div className="gwNotice isSoft">Configure permission is required to create or control this edge server.</div> : null}
              <div className="plcProtocolSaveRow">
                <div>
                  <strong>{reviewValid ? reviewPresentation.title : "Draft needs attention"}</strong>
                  <span>{reviewValid ? reviewPresentation.message : reviewMessage}</span>
                </div>
                <button className="gwPrimaryBtn" type="button" disabled={!editable || reviewMode === "disabled" || !reviewValid} onClick={previewConfiguration}>{busy === "preview" ? "Preparing review…" : reviewPresentation.button}</button>
              </div>
            </div>
          </div>
        </>
      ) : null}

      {history.length ? (
        <div className="gwCardSection">
          <div className="gwSectionTitle">Revision history</div>
          <div className="gwCardSectionBody">
            {history.map((revision) => (
              <div className="gwEdgeFieldbusRow" key={revision.revision}>
                <div>
                  <div className="gwListTitle">Revision {revision.revision}</div>
                  <div className="gwListMeta">{revision.desired_state === "running" ? "Listening" : "Stopped"} · {revision.status || "Unknown"} · {formatDateTime(revision.created_at)}</div>
                  {edgeModbusSecurityHistoryLabel(revision) ? <div className="gwListMeta">{edgeModbusSecurityHistoryLabel(revision)}</div> : null}
                </div>
                {revision.status === "applied" ? <button className="gwRowAction" type="button" disabled={!canConfigure || (revision.desired_state === "running" && !readiness.ready) || !!busy} onClick={() => previewRollback(revision.revision)}>{busy === `rollback:${revision.revision}` ? "Preparing…" : "Review rollback…"}</button> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <EdgeModbusServerReview
        review={review}
        gatewayName={gateway.name}
        busy={busy === "confirm"}
        acknowledged={reviewAcknowledged}
        catalogReview={review ? edgeModbusCatalogReviewForReview(review, tagCatalog, tagEvidenceByPath) : null}
        onAcknowledgedChange={setReviewAcknowledged}
        onCancel={() => { setReview(null); setReviewAcknowledged(false); }}
        onConfirm={confirmReview}
      />
    </div>
  );
}

export { publicConfigForm };
